import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ChangePasswordInteractor from "../../interactor/authentication/ChangePasswordInteractor";
import ValidatableObject from "../../model/validation/ValidatableObject";
import RequiredValueValidator from "../../model/validation/validators/RequiredValueValidator";
import PasswordMatchValidator from "../../model/validation/validators/PasswordMatchValidator";
import { TValidationResult } from "../../model/validation/ValidationResult";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import { TLoginPresenter } from "./LoginPresenter";
import LoginInteractor from "../../interactor/authentication/LoginInteractor";
import ResetPasswordInteractor from "../../interactor/authentication/ResetPasswordInteractor";

export interface TChangePasswordPresenter extends TLoadingAwarePresenter {
  passwordPresentationModel: TPasswordChangePresentationModel;
  validationPasswordErrors: any;
  isChangeButtonDisabled: boolean;
  username?: string;
  isPasswordExpired?: boolean;
  buttonDisabled?: boolean;
  token?: string;
}
export interface IChangePasswordPresenter
  extends TChangePasswordPresenter,
    TPresentable {
  changePassword(): void;
  resetPassword(): void;
  setUsername(value: string): void;
  setPasswordValue(key: string, value: string): void;
  showLogin(): void;
}

export interface TPasswordChangePresentationModel {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const PasswordChangePresentationModel = (data: any) => {
  return {
    oldPassword: data.oldPassword,
    newPassword: data.newPassword,
    confirmNewPassword: data.confirmNewPassword
  };
};

const defaultState: TChangePasswordPresenter = {
  passwordPresentationModel: {} as TPasswordChangePresentationModel,
  validationPasswordErrors: {},
  isChangeButtonDisabled: false,
  isPasswordExpired: false,
  buttonDisabled: true
};

const ChangePasswordPresenter = withStore<
  IChangePasswordPresenter,
  TChangePasswordPresenter
>(({ application, store, loader, translate }): IChangePasswordPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TChangePasswordPresenter>();

  const hasErrors = (object) => {
    return Object.entries(object).length;
  };

  const changePassword = async () => {
    try {
      const passwordPresentationModel =
        _store.getState<TChangePasswordPresenter>().passwordPresentationModel;
      const validationResult = await validatePassword(
        passwordPresentationModel
      );
      const username = _store.getState<TChangePasswordPresenter>().username;
      const token = _store.getState<TChangePasswordPresenter>().token;
      if (validationResult && validationResult.isValid) {
        loader.start("changePasswordLoader");
        await _application.container
          .resolve<ChangePasswordInteractor>("changePassword")
          .execute(
            passwordPresentationModel.newPassword,
            passwordPresentationModel.oldPassword,
            username,
            token
          );
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("login.change_password_success_message"));
        _store.update({
          passwordPresentationModel: {} as TPasswordChangePresentationModel,
          isChangeButtonDisabled: false
        });
        loader.stop("changePasswordLoader");
        const isPasswordExpired =
          _store.getState<TChangePasswordPresenter>().isPasswordExpired;
        if (isPasswordExpired && username) {
          await _application.container
            .resolve<LoginInteractor>("login")
            .execute(username, passwordPresentationModel.newPassword);
        }
        if (token) {
          _application.navigator.replace({
            pathname: `/login`
          });
        }
      }
    } catch (error) {
      loader.stop("changePasswordLoader");
    }
  };

  const setPasswordValue = (key: string, value: string) => {
    const passwordPresentationModel =
      _store.getState<TChangePasswordPresenter>().passwordPresentationModel;
    const validationErrors =
      _store.getState<TChangePasswordPresenter>().validationPasswordErrors;
    passwordPresentationModel[key] = value;
    _store.update({
      passwordPresentationModel
    });
    checkChangeButtonDisabled();
    return (
      hasErrors(validationErrors) && validatePassword(passwordPresentationModel)
    );
  };

  const setUsername = (value: String) => {
    _store.update({
      username: value,
      buttonDisabled: value == null
    });
  };

  const resetPassword = async () => {
    const { username } = _store.getState<TChangePasswordPresenter>();
    if (username) {
      await _application.container
        .resolve<ResetPasswordInteractor>("resetPassword")
        .execute(username);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("login.reset_password_success_message"));
      _application.navigator.push({
        pathname: `/login`
      });
    }
  };

  const validatePassword = async (password) => {
    const token = _store.getState<TChangePasswordPresenter>().token;
    const validatableObject = ValidatableObject(
      PasswordChangePresentationModel(password)
    );
    if (!token) {
      validatableObject.addValidator(RequiredValueValidator("oldPassword"));
    }
    validatableObject.addValidator(RequiredValueValidator("newPassword"));
    validatableObject.addValidator(
      RequiredValueValidator("confirmNewPassword")
    );
    validatableObject.addValidator(
      PasswordMatchValidator("confirmNewPassword", "newPassword")
    );
    const result: TValidationResult = await validatableObject.validate();
    _store.update({
      validationPasswordErrors: result.validationErrors,
      isChangeButtonDisabled: hasErrors(result.validationErrors) ? true : false
    });
    return result;
  };

  const checkChangeButtonDisabled = () => {
    const validationErrors =
      _store.getState<TChangePasswordPresenter>().validationPasswordErrors;
    if (hasErrors(validationErrors)) {
      return _store.update({ isChangeButtonDisabled: true });
    }
    return _store.update({ isChangeButtonDisabled: false });
  };

  const showLogin = () => {
    const username = _store.getState<TLoginPresenter>().username;
    return _application.navigator.replace({
      pathname: "/login",
      state: { username } as TLoginPresenter
    });
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    changePassword,
    setPasswordValue,
    resetPassword,
    setUsername,
    showLogin
  };
}, defaultState);

export default ChangePasswordPresenter;
